import React, { useEffect, useState } from 'react'
import _get from 'lodash/get'
import _map from 'lodash/map'

import Button from '../Button'
import CustomFieldsTable from './CustomFieldsTable'
import CustomFieldModal from './CustomFieldModal'
import { FIELDS } from './utils/constants'
import { getCustomFields, createCustomFields, deleteCustomFields } from '../../../_common/core/http_services'
import { showAxiosError } from '../../utils/messenger'
import ConfirmModal from '../ConfirmModal'

const fieldsDataAdapter = ressponseDate => {
  const attributes = _get(ressponseDate, 'data.data.attributes', [])
  const fieldsData = []

  attributes.forEach(item => {
    for (const key in item) {
      fieldsData.push({
        type: key,
        fields: _map(item[key].group.fields, fieldItem => {
          const updatedFieldItem = { ...fieldItem }

          if (fieldItem.type === FIELDS.RADIO.key) {
            updatedFieldItem[FIELDS.RADIO.optionsKey] = fieldItem.options
          }
          if (fieldItem.type === FIELDS.SELECT.key) {
            updatedFieldItem[FIELDS.SELECT.optionsKey] = fieldItem.options
          }
          if (fieldItem.type === FIELDS.SELECT_MULTI.key) {
            updatedFieldItem[FIELDS.SELECT_MULTI.optionsKey] = fieldItem.options
          }

          delete updatedFieldItem.options
          updatedFieldItem.defaultValue = Array.isArray(updatedFieldItem.defaultValue)
            ? updatedFieldItem.defaultValue?.[0] ?? ""
            : updatedFieldItem.defaultValue

          return updatedFieldItem
        }),
      })
    }
  })

  return fieldsData.sort((a, b) => {
    const aValue = a.type === 'order' ? 2 : a.type === 'ticket' ? 1 : 0
    const bValue = b.type === 'order' ? 2 : b.type === 'ticket' ? 1 : 0
    return bValue - aValue
  })
}

const updateOptions = values => {
  if (values.type === FIELDS.RADIO.key) {
    values.options = values[FIELDS.RADIO.optionsKey]
  }
  if (values.type === FIELDS.SELECT.key) {
    values.options = values[FIELDS.SELECT.optionsKey]
  }
  if (values.type === FIELDS.SELECT_MULTI.key) {
    values.options = values[FIELDS.SELECT_MULTI.optionsKey]
  }
}

const CustomFieldsContainer = props => {
  const { eventId } = props
  const [customFieldsData, setCustomFieldsData] = useState([])
  const [selectedField, setSelectedField] = useState(null)
  const [isCustomFieldModalOpen, setIsCustomFieldModalOpen] = useState(false)
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [confirmDeleteSingleFieldModalOpen, setConfirmDeleteSingleFieldModalOpen] = useState(false)

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const res = await getCustomFields(eventId)
        const fieldsData = fieldsDataAdapter(res)
        setCustomFieldsData(fieldsData)
      } catch (error) {
        showAxiosError(error)
      }
    }

    fetchCustomFields()
  }, [eventId])

  const handleSubmit = async (values, isDeletingSingleField) => {
    try {
      const updatedCustomFieldsData = [...customFieldsData]
      updatedCustomFieldsData.forEach(item => {
        item.fields.forEach(field => {
          updateOptions(field)
        })
      })

      const data = []

      const existingType = updatedCustomFieldsData.find(item => item.type === values.parentType)
      const existingCustomField = existingType?.fields.find(fieldItem => fieldItem.id === values?.id)

      const updatedValues = { ...values }
      updateOptions(updatedValues)
      delete updatedValues.parentType
      delete updatedValues[FIELDS.RADIO.optionsKey]
      delete updatedValues[FIELDS.SELECT.optionsKey]
      delete updatedValues[FIELDS.SELECT_MULTI.optionsKey]

      if (!existingType) {
        const fields = [updatedValues]

        updatedCustomFieldsData.push({
          type: values.parentType,
          fields,
        })
      } else {
        const { fields } = existingType
        const selectedFieldIndex = fields.findIndex(fieldItem => fieldItem.id === values?.id)

        if (!existingCustomField) {
          fields.push(updatedValues)
        } else if (isDeletingSingleField) {
          fields.splice(selectedFieldIndex, 1)
        } else {
          fields[selectedFieldIndex] = updatedValues
        }
      }

      updatedCustomFieldsData.forEach(item => {
        if (item.fields.length) {
          data.push({
            attributes: { ...item },
            type: 'custom_fields',
            relationships: [],
          })
        }
      })

      await deleteCustomFields(eventId)
      data.forEach(item => {
        item.attributes.fields.forEach(field => {
          delete field.id

          field.options?.forEach(option => {
            delete option.id
          })
        })
      })

      const res = await createCustomFields(eventId, { data })
      const fieldsData = fieldsDataAdapter(res)

      setSelectedField(null)
      setCustomFieldsData(fieldsData)
      setIsCustomFieldModalOpen(false)
      setConfirmDeleteSingleFieldModalOpen(false)
    } catch (error) {
      showAxiosError(error)
    }
  }

  const handleDeleteAllCustomFields = async () => {
    try {
      await deleteCustomFields(eventId)
      setCustomFieldsData([])
      setConfirmDeleteModalOpen(false)
    } catch (error) {
      showAxiosError(error)
    }
  }

  const handleEditClick = fieldData => {
    setSelectedField(fieldData)
    setIsCustomFieldModalOpen(true)
  }

  const handleDeleteSingleField = fieldData => {
    handleSubmit(fieldData, true)
  }

  return (
    <div>
      <div className="btn-toolbar btn_holder">
        <Button
          className="btn btn-ok"
          type="button"
          onClick={() => {
            setIsCustomFieldModalOpen(true)
          }}
        >
          <i className={'fa fa-fw fa-plus'} />
          Create Custom Field
        </Button>
        <Button
          className="btn btn-danger"
          type="button"
          disabled={!customFieldsData.length}
          onClick={() => {
            setConfirmDeleteModalOpen(true)
          }}
        >
          <i className={'fa fa-fw fa-trash'} />
          Delete All Custom Fields
        </Button>
      </div>
      <CustomFieldModal
        isOpen={isCustomFieldModalOpen}
        customFieldsData={customFieldsData}
        fieldData={selectedField}
        onSubmit={values => {
          handleSubmit(values)
        }}
        onClose={() => {
          setIsCustomFieldModalOpen(false)
          setSelectedField(null)
        }}
      />

      <ConfirmModal
        content="This action will delete all custom fields."
        isOpen={confirmDeleteModalOpen}
        onClose={() => {
          setConfirmDeleteModalOpen(false)
        }}
        onAction={action => {
          switch (action) {
            case 'delete': {
              handleDeleteAllCustomFields()
              break
            }
            default: {
              setConfirmDeleteModalOpen(false)
            }
          }
        }}
        actions={[
          { value: 'delete', label: 'Delete', className: 'btn btn-danger' },
          { value: 'cancel', label: 'Cancel', className: 'btn btn-cancel' },
        ]}
        classNames={{
          modal: 'modal-dialog modal-trans modal-medium',
          container: 'modal-dialog modal-medium',
        }}
      />

      <ConfirmModal
        content="This action will delete custom field."
        isOpen={confirmDeleteSingleFieldModalOpen}
        onClose={() => {
          setConfirmDeleteSingleFieldModalOpen(false)
        }}
        onAction={action => {
          switch (action) {
            case 'delete': {
              handleDeleteSingleField(selectedField)
              break
            }
            default: {
              setConfirmDeleteSingleFieldModalOpen(false)
            }
          }
        }}
        actions={[
          { value: 'delete', label: 'Delete', className: 'btn btn-danger' },
          { value: 'cancel', label: 'Cancel', className: 'btn btn-cancel' },
        ]}
        classNames={{
          modal: 'modal-dialog modal-trans modal-medium',
          container: 'modal-dialog modal-medium',
        }}
      />

      <div className="customfields-form">
        {_map(customFieldsData, fieldItem => {
          const fieldsData = customFieldsData.filter(item => item.type === fieldItem.type)[0].fields

          return (
            <CustomFieldsTable
              key={fieldItem.type}
              data={fieldsData}
              type={fieldItem.type}
              handleEditClick={handleEditClick}
              handleDeleteClick={field => {
                setSelectedField(field)
                setConfirmDeleteSingleFieldModalOpen(true)
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

export default CustomFieldsContainer
